'use client'

import { useEffect, useRef, useState, useTransition } from 'react'

import { useRouter, useSearchParams } from 'next/navigation'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'

import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import HomeIcon from '@mui/icons-material/Home'
import PrintIcon from '@mui/icons-material/Print'
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  styled,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import axios from 'axios'
import dayjs from 'dayjs'
import { FastAverageColor } from 'fast-average-color'
import { AnimatePresence, motion } from 'framer-motion'
import jsPDF from 'jspdf'
import type { MRT_RowData } from 'material-react-table'



import UcapanDukaMediaCard from './UcapanDukaMediaCard'

const isVideoFile = (url: string) => {
  const videoExtensions = ['mp4', 'webm', 'ogg', 'mov']
  const extension = url.split('.').pop()?.toLowerCase()

  
return extension ? videoExtensions.includes(extension) : false
}

import type { Socket } from 'socket.io-client';
import io from 'socket.io-client'

import showToastAlert from '@components/Toast'
import ProgressBar from '@/components/ProgressBar'
import CustomButton from '@/components/layout/shared/CustomButton'
import ConfirmationDialogYes from '@/components/layout/shared/ConfirmationDialogYes'
import ConfirmationDialog from '@/components/layout/shared/ConfirmationDialog'

// Professional styled components
const StepperContainer = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  border: '1px solid rgba(0,0,0,0.06)'
}))

const ContentCard = styled(motion.div)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  border: '1px solid rgba(0,0,0,0.06)',
  minHeight: '400px'
}))

const NavigationBar = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
  zIndex: 1100,
  padding: theme.spacing(2),
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)'
}))

const DesignCard = styled(motion.div)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  position: 'relative',
  cursor: 'pointer',
  border: '2px solid transparent',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.15)'
  }
}))

const PreviewContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '400px',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  border: '1px solid rgba(0,0,0,0.08)'
}))

const PackageCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  border: '2px solid transparent',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
  }
}))

const CategoryToggle = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: theme.spacing(1.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateX(4px)'
  }
}))

const condolencePhrases = [
  'Turut Berduka Cita',
  'Simpati Kami yang Terdalam',
  'Ucapan Belasungkawa',
  'Duka yang Mendalam',
  'Kenangan Abadi',
  'Doa dan Cinta Kami Menyertaimu',
  'Selamat Jalan dalam Damai',
  'Istirahatlah dengan Tenang',
  'Dalam Doa dan Cinta',
  'Kami Bersama dalam Duka',
  'Kepergianmu Meninggalkan Duka',
  'Hati Kami Bersamamu',
  'Dalam Ingatan dan Doa',
  'Beristirahatlah dalam Kasih Tuhan',
  'Doa Kami Menyertaimu',
  'Selamat Jalan Menuju Kehidupan Abadi',
  'Semoga Damai Menyertaimu',
  'Kami Kehilangan, Tapi Surga Mendapatkan',
  'Terima Kasih Atas Segalanya',
  'Semoga Tenang di Sisi-Nya',
  'Kehilanganmu Sangat Kami Rasakan',
  'Kami Doakan yang Terbaik untuk Arwahmu',
  'Semoga Jalanmu Terang Menuju Surga',
  'Dalam Pelukan Tuhan yang Abadi',
  'Kenangan Indah Akan Selalu Hidup'
]

const ViewPage = ({
  urlAPI,
  kategoriTemplateList,
  hargaUcapanList,
  rekeningList
}: {
  urlAPI: string
  kategoriTemplateList: any
  hargaUcapanList: any
  rekeningList: any
}) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [page, setPage] = useState(1)
  const [noInvoice, setNoInvoice] = useState('')
  const [rowsPerPage, setRowsPerPage] = useState(1000)
  const [rows, setRows] = useState<MRT_RowData[]>([])
  const [selectedCard, setSelectedCard] = useState<string | null>(null)
  const [dataRegis, setDataRegis] = useState<any>(null)
  const [selectedPackage, setSelectedPackage] = useState<string>('')
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const [selectedKategori, setSelectedKategori] = useState<string[]>([])
  const [editDukaId, setEditDukaId] = useState<string | null>(null)
  const steps = ['Data Pemesan', 'Pilih Desain', 'Atur Pesan', 'Pilih Paket', 'Pembayaran', 'Konfirmasi', 'Selesai']
  const [activeStep, setActiveStep] = useState(0)
  const searchParams = useSearchParams()
  const socketRef = useRef<Socket | null>(null)

  useEffect(() => {
    socketRef.current = io(process.env.NEXT_PUBLIC_NEXT_SOCKET as string, {
      transports: ['websocket']
    })

    socketRef.current.on('connect', () => {
      socketRef.current?.emit('channel', 'ETERNAL-HOME-FUNERAL')
      console.log('Socket connected')
    })
    socketRef.current.on('connect_error', err => {
      console.log('Connection error:', err.message)
    })

    return () => {
      socketRef.current?.disconnect()
    }
  }, [])

  const [detailForm, setDetailForm] = useState({
    title: 'Turut Berduka Cita',
    keteranganPenerima: '',
    keteranganPengirim: '',
    sender: ''
  })

  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    alamat: ''
  })

  const [selectedBank, setSelectedBank] = useState<string>('')
  const [paymentProof, setPaymentProof] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>('')

  useEffect(() => {
    if (kategoriTemplateList.length > 0) {
      const allIds = kategoriTemplateList.map((k: any) => String(k.ID_KATAGORI))

      setSelectedKategori(allIds) // default: semua kepilih
    }
  }, [kategoriTemplateList])

  const handleToggle = (id: string) => {
    setSelectedKategori(prev => {
      const allIds = kategoriTemplateList.map((k: any) => String(k.ID_KATAGORI)).filter((k: any) => k !== '0')

      if (id === '0') {
        const isAllSelected = prev.includes('0')

        if (isAllSelected) {
          // Jangan kosongkan jika hanya '0' yang dipilih
          return prev.length > 1 ? [] : prev
        } else {
          return ['0', ...allIds]
        }
      } else {
        const filteredPrev = prev.filter(item => item !== '0')
        let updated

        if (filteredPrev.includes(id)) {
          updated = filteredPrev.filter(item => item !== id)


          // Jangan biarkan kosong
          if (updated.length === 0) {
            return prev
          }
        } else {
          updated = [...filteredPrev, id]
        }

        const isNowAllSelected = allIds.every((id: any) => updated.includes(id))

        if (isNowAllSelected) {
          return ['0', ...updated]
        }

        return updated
      }
    })
  }

  // useEffect(() => {
  //   const storedData = sessionStorage.getItem('dataRegis')
  //   if (storedData) {
  //     setDataRegis(JSON.parse(storedData))
  //   }
  // }, [])
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    const unitParam = searchParams.get('id')

    if (unitParam) {
      cariNoRegTagihan({ NO_REG: unitParam })
    }
  }, [searchParams])

  const cariNoRegTagihan = async (row: any) => {
    setLoading(true)

    try {
      const response = await axios.post(urlAPI + 'tagihan/carinoregtagihan', {
        noreg: row.NO_REG,
        idunit: '0',
        idinv: '0'
      })

      if (response.data.metadata.code === 200) {
        // setDialogOpen(true)
        cariNoReg(row.NO_REG)
      } else {
        setDialogOpen(true)
      }
    } catch (error: any) {
      console.error('Error login:', error)
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  const cariNoReg = async (NO_REG: string) => {
    setLoading(true)

    try {
      const response = await axios.post(urlAPI + 'registrasi/carinoreg', {
        noreg: NO_REG
      })

      if (response.data.metadata.code === 200) {
        setDataRegis(response.data.response.list[0])
      }
    } catch (error: any) {
      console.error('Error login:', error)
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (selectedKategori.length > 0) {
      getData(1)
    }
  }, [selectedKategori])

  const getData = async (jenis: number) => {
    setLoading(true)
    const formattedKategori = `(${selectedKategori.join(',')})`

    try {
      const response = await axios.post(urlAPI + 'ref/gambartempletelist', {
        idkatagori: formattedKategori,
        idunit: '001',
        nama: '',
        halaman: page - 1,
        baris: rowsPerPage
      })

      if (response.data.metadata.code === 200) {
        const fetchedData = response.data.response.list

        setRows(fetchedData)
      } else {
        setRows([])
      }
    } catch (error: any) {
      console.error('Error login:', error)
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate personal information
      if (!personalInfo.firstName || !personalInfo.phone) {
        showToastAlert('info', 'Silakan lengkapi semua informasi pribadi')
        
return
      }


      // Validate email format
      // if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personalInfo.email)) {
      //   showToastAlert('info', 'Format email tidak valid')
      //   return
      // }
      // Validate phone number format
      if (!/^08\d{8,11}$/.test(personalInfo.phone)) {
        showToastAlert('info', 'Format nomor telepon tidak valid')
        
return
      }
    }

    if (activeStep === 1 && !selectedCard) {
      showToastAlert('info', 'Silakan pilih desain terlebih dahulu')
      
return
    }

    if (activeStep === 2 && !detailForm.sender) {
      showToastAlert('info', 'Silakan isi nama pengirim')
      
return
    }

    if (activeStep === 3 && !selectedPackage) {
      showToastAlert('info', 'Silakan pilih paket durasi')
      
return
    }

    if (activeStep === 4) {
      // Validate payment info
      if (!selectedBank) {
        showToastAlert('info', 'Silakan pilih metode pembayaran')
        
return
      }

      if (editDukaId) {
        if (!paymentProof) {
          if (!previewUrl) {
            showToastAlert('info', 'Silakan upload bukti pembayaran')
            
return
          }
        }
      } else {
        if (!paymentProof) {
          showToastAlert('info', 'Silakan upload bukti pembayaran')
          
return
        }
      }
    }

    if (activeStep === 5) {
      // Confirmation step - call API here
      handleSaveOrder()
      
return
    }

    setActiveStep(prevActiveStep => prevActiveStep + 1)
  }


  // Add this function to handle the API call
  const handleSaveOrder = async () => {
    // Validation checks
    if (!selectedCard) {
      showToastAlert('info', 'Silakan pilih desain terlebih dahulu')
      
return
    }

    if (!detailForm.title) {
      showToastAlert('info', 'Silakan pilih kata-kata ucapan duka')
      
return
    }

    if (!detailForm.sender) {
      showToastAlert('info', 'Silakan isi nama pengirim')
      
return
    }

    if (!selectedPackage) {
      showToastAlert('info', 'Silakan pilih paket durasi')
      
return
    }

    if (!personalInfo.firstName) {
      showToastAlert('info', 'Silakan lengkapi nama pemesan')
      
return
    }

    // if (!personalInfo.email) {
    //   showToastAlert('info', 'Silakan isi email pemesan')
    //   return
    // }

    // // Email format validation
    // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    // if (!emailRegex.test(personalInfo.email)) {
    //   showToastAlert('info', 'Format email tidak valid')
    //   return
    // }

    if (!personalInfo.phone) {
      showToastAlert('info', 'Silakan isi nomor telepon pemesan')
      
return
    }

    if (!personalInfo.alamat) {
      showToastAlert('info', 'Silakan isi alamat pemesan')
      
return
    }

    // Phone number format validation
    const phoneRegex = /^08\d{8,11}$/

    if (!phoneRegex.test(personalInfo.phone)) {
      showToastAlert('info', 'Format nomor telepon tidak valid')
      
return
    }

    if (!selectedBank) {
      showToastAlert('info', 'Silakan pilih metode pembayaran')
      
return
    }

    if (editDukaId) {
      if (!paymentProof) {
        if (!previewUrl) {
          showToastAlert('info', 'Silakan upload bukti pembayaran')
          
return
        }
      }
    } else {
      if (!paymentProof) {
        showToastAlert('info', 'Silakan upload bukti pembayaran')
        
return
      }
    }

    try {
      setLoading(true)

      // Convert payment proof to base64
      const base64Image: string = await new Promise(resolve => {
        if (paymentProof) {
          const reader = new FileReader()

          reader.onloadend = () => resolve(reader.result as string)
          reader.readAsDataURL(paymentProof)
        } else {
          resolve('')
        }
      })

      const selectedTemplate = rows.find(row => row.NO_GAMBAR === selectedCard)
      const selectedPricePackage = hargaUcapanList.find((item: any) => item.ID_HARGA === selectedPackage)
      const selectedRekening = rekeningList.find((item: any) => item.NOREK === selectedBank)

      const payload = {
        idunit: '001',
        noreg: dataRegis?.NO_REG,
        nogambar: selectedCard,
        gambar: selectedTemplate?.GAMBAR,
        idkategori: selectedTemplate?.ID_KATAGORI,
        judulucapan: detailForm.title,
        penerima: dataRegis?.NAMA_M,
        ketpenerima: detailForm.keteranganPenerima,
        pengirim: detailForm.sender,
        ketpengirim: detailForm.keteranganPengirim,
        idharga: selectedPricePackage?.ID_HARGA,
        harga: selectedPricePackage?.HARGA,
        durasi: Number(selectedPricePackage?.KETERAANGAN),

        // Pemesan info
        namapengirim: personalInfo.firstName,
        email: personalInfo.email,
        alamat: personalInfo.alamat,
        nohp: personalInfo.phone,
        norek: selectedRekening?.NOREK || '',
        bukti: base64Image ? base64Image.split(',')[1] : '',
        noduka: editDukaId || ''
      }

      if (editDukaId) {
        payload.noduka = editDukaId
      }

      let response

      if (editDukaId) {
        payload.noduka = editDukaId
        response = await axios.post(urlAPI + 'registrasi/updatedukacita', payload)
      } else {
        response = await axios.post(urlAPI + 'registrasi/savedukacita', payload)
      }

      if (response.data.metadata.code === 200) {
        setNoInvoice(response.data.response.noduka)
        setEditDukaId('')
        setActiveStep(6) // Move to success step
        showToastAlert('success', 'Pesanan berhasil dibuat')

        const waNumber = '6282118546605'

        const message = `Halo Admin, saya baru saja melakukan pemesanan ucapan duka.

Detail Pesanan:
No. Invoice: ${response.data.response.noduka}
Nama Pemesan: ${personalInfo.firstName}
No. HP Pemesan: ${personalInfo.phone}
Judul Ucapan: ${detailForm.title}
Nama Penerima: ${dataRegis?.NAMA_M || '-'}
Nama Pengirim (di ucapan): ${detailForm.sender}

Mohon bantuannya untuk diproses. Terima kasih.`

        window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`, '_blank')

        if (!socketRef.current || !socketRef.current.connected) {
          console.warn('Socket not connected yet')
          
return
        }

        socketRef.current?.emit('message', {
          channel: 'ETERNAL-HOME-FUNERAL',
          message: response.data.response.noduka
        })
      } else {
        showToastAlert('error', response.data.metadata.message)
      }
    } catch (error: any) {
      console.error('Error saving order:', error)
      showToastAlert('error', error.response?.data?.metadata?.message || 'Terjadi kesalahan saat menyimpan pesanan')
    } finally {
      setLoading(false)
    }
  }

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1)
  }

  const toHome = () => {
    sessionStorage.clear()

    startTransition(() => {
      router.push(`/`)
    })
  }

  const handleFormChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDetailForm(prev => ({
      ...prev,
      [field]: event.target.value
    }))
  }

  const handlePersonalInfoChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPersonalInfo(prev => ({
        ...prev,
        [field]: event.target.value
      }))
    }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (file) {
      // File size validation
      if (file.size > 5 * 1024 * 1024) {
        showToastAlert('info', 'Ukuran file maksimal 5MB')
        
return
      }

      // File type validation
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        showToastAlert('info', 'Format file harus JPG atau PNG')
        
return
      }

      // Convert to base64
      const reader = new FileReader()

      reader.onloadend = () => {
        const base64String = reader.result as string


        // You can store base64 in state if needed
        setPaymentProof(file)
        setPreviewUrl(URL.createObjectURL(file))

        // Optional: store base64 in state
        // setBase64Image(base64String)
      }

      reader.readAsDataURL(file)
    }
  }

  const generatePDF = async (orderData: any) => {
    // Create new PDF document (A5 size)
    const pdf = new jsPDF('p', 'mm', 'a5')

    // Set document properties
    pdf.setProperties({
      title: 'Bukti Pembayaran Digital Funeral',
      subject: 'Bukti Pembayaran',
      author: 'Digital Funeral TMC Eternal Home',
      keywords: 'bukti, pembayaran, funeral',
      creator: 'Digital Funeral System'
    })

    // Add company logo/header
    pdf.setFillColor(45, 55, 72) // Dark blue header
    pdf.rect(0, 0, 148, 30, 'F')

    // Add white text for header
    pdf.setTextColor(255, 255, 255)
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(16)
    pdf.text('Digital Funeral TMC Eternal Home', 74, 12, { align: 'center' })
    pdf.setFontSize(12)
    pdf.text('Bukti Pembayaran', 74, 20, { align: 'center' })

    // Reset text color to black
    pdf.setTextColor(0, 0, 0)

    // Add order details
    pdf.setFontSize(10)
    pdf.setFont('helvetica', 'normal')

    const startY = 40
    const lineHeight = 7
    let currentY = startY

    // Order number
    pdf.setFont('helvetica', 'bold')
    pdf.text('No. Order:', 15, currentY)
    pdf.setFont('helvetica', 'normal')
    pdf.text(`#${noInvoice}`, 50, currentY)
    currentY += lineHeight

    // Date
    pdf.setFont('helvetica', 'bold')
    pdf.text('Tanggal:', 15, currentY)
    pdf.setFont('helvetica', 'normal')
    pdf.text(new Date().toLocaleDateString('id-ID'), 50, currentY)
    currentY += lineHeight * 2

    // Divider line
    pdf.setLineWidth(0.2)
    pdf.line(15, currentY - 3, 133, currentY - 3)
    currentY += lineHeight

    // Customer details
    pdf.setFont('helvetica', 'bold')
    pdf.text('Detail Pemesan', 15, currentY)
    currentY += lineHeight

    pdf.setFont('helvetica', 'normal')
    pdf.text(`Nama: ${orderData.personalInfo.firstName} ${orderData.personalInfo.lastName}`, 15, currentY)
    currentY += lineHeight
    pdf.text(`Email: ${orderData.personalInfo.email}`, 15, currentY)
    currentY += lineHeight
    pdf.text(`Telepon: ${orderData.personalInfo.phone}`, 15, currentY)
    currentY += lineHeight * 2

    // Order details
    pdf.setFont('helvetica', 'bold')
    pdf.text('Detail Pesanan', 15, currentY)
    currentY += lineHeight

    pdf.setFont('helvetica', 'normal')
    pdf.text(`Ucapan: ${orderData.detailForm.title}`, 15, currentY)
    currentY += lineHeight
    pdf.text(`Penerima: ${orderData.dataRegis.NAMA_M}`, 15, currentY)
    currentY += lineHeight
    pdf.text(`Pengirim: ${orderData.detailForm.sender}`, 15, currentY)
    currentY += lineHeight * 2

    // Payment details
    pdf.setFont('helvetica', 'bold')
    pdf.text('Detail Pembayaran', 15, currentY)
    currentY += lineHeight

    pdf.setFont('helvetica', 'normal')
    const bank = orderData.rekeningList.find((b: any) => b.NOREK === orderData.selectedBank)

    pdf.text(`Bank: ${bank?.NAMABANK}`, 15, currentY)
    currentY += lineHeight
    pdf.text(`No. Rekening: ${bank?.NOREK}`, 15, currentY)
    currentY += lineHeight
    pdf.text(`Atas Nama: ${bank?.NAMAREK}`, 15, currentY)
    currentY += lineHeight

    // Total payment
    currentY += lineHeight
    pdf.setFillColor(45, 55, 72)
    pdf.rect(15, currentY - 5, 118, 10, 'F')
    pdf.setTextColor(255, 255, 255)
    pdf.setFont('helvetica', 'bold')
    pdf.text('Total Pembayaran:', 20, currentY)

    const total = `Rp ${parseInt(
      orderData.hargaUcapanList.find((item: any) => item.ID_HARGA === orderData.selectedPackage)?.HARGA
    ).toLocaleString('id-ID')}`

    pdf.text(total, 128, currentY, { align: 'right' })

    // Add footer
    pdf.setTextColor(128, 128, 128)
    pdf.setFontSize(8)
    pdf.text('TMC Eternal Home © ' + dayjs().format('YYYY'), 74, 205, { align: 'center' })

    // Save the PDF
    pdf.save('bukti-pembayaran-digital-funeral.pdf')
  }

  const [textColor, setTextColor] = useState('white')

  useEffect(() => {
    if (!selectedCard) return

    const img = new Image()

    img.onload = () => {
      try {
        const fac = new FastAverageColor()
        const color = fac.getColor(img)
        const brightness = (color.value[0] * 299 + color.value[1] * 587 + color.value[2] * 114) / 1000

        setTextColor(brightness > 128 ? 'black' : 'white')
      } catch (err) {
        console.error('FastAverageColor error:', err)
        setTextColor('white')
      }
    }

    img.onerror = () => {
      setTextColor('white')
    }

    // Gunakan proxy endpoint
    const originalUrl =
      process.env.NEXT_PUBLIC_URL_IMAGE_TEMPLATE + (rows.find(row => row.NO_GAMBAR === selectedCard)?.GAMBAR || '')

    img.src = `/api/proxy-image?url=${encodeURIComponent(originalUrl)}`
  }, [selectedCard, rows])

  // useEffect(() => {
  //   if (!selectedCard) return

  //   // Tunggu sebentar untuk memastikan gambar sudah di-render di DOM
  //   setTimeout(() => {
  //     // Cari elemen img yang sudah ada di halaman
  //     const existingImg = document.querySelector(`img[src*="${selectedCard}"]`) as HTMLImageElement

  //     if (existingImg && existingImg.complete) {
  //       // Gambar sudah loaded
  //       try {
  //         const fac = new FastAverageColor()
  //         const color = fac.getColor(existingImg)
  //         const { value } = color
  //         const [red, green, blue] = value
  //         const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255

  //         if (luminance > 0.7) {
  //           setTextColor('#000000')
  //         } else if (luminance > 0.5) {
  //           setTextColor('#1a1a1a')
  //         } else if (luminance > 0.3) {
  //           setTextColor('#ffffff')
  //         } else {
  //           setTextColor('#ffffff')
  //         }
  //       } catch (err) {
  //         console.error('FastAverageColor error:', err)
  //         setTextColor('#ffffff')
  //       }
  //     } else {
  //       // Fallback: buat img element baru tanpa crossOrigin
  //       const img = new Image()

  //       img.onload = () => {
  //         try {
  //           const fac = new FastAverageColor()
  //           const color = fac.getColor(img)
  //           const { value } = color
  //           const [red, green, blue] = value
  //           const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255

  //           if (luminance > 0.7) {
  //             setTextColor('#000000')
  //           } else if (luminance > 0.5) {
  //             setTextColor('#1a1a1a')
  //           } else if (luminance > 0.3) {
  //             setTextColor('#ffffff')
  //           } else {
  //             setTextColor('#ffffff')
  //           }
  //         } catch (err) {
  //           console.error('FastAverageColor error:', err)
  //           setTextColor('#ffffff')
  //         }
  //       }

  //       img.onerror = e => {
  //         console.error('Failed to load image:', e)
  //         setTextColor('#ffffff')
  //       }

  //       // Tanpa crossOrigin untuk menghindari CORS
  //       const originalUrl =
  //         process.env.NEXT_PUBLIC_URL_IMAGE_TEMPLATE + (rows.find(row => row.NO_GAMBAR === selectedCard)?.GAMBAR || '')
  //       img.src = originalUrl
  //     }
  //   }, 500) // Delay 500ms untuk memastikan DOM sudah ter-render
  // }, [selectedCard, rows])
  const [phoneLoading, setPhoneLoading] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const [historyList, setHistoryList] = useState<any[]>([])
  const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false)
  const [deleteDukaId, setDeleteDukaId] = useState<string | null>(null)

  const renderPreview = () => (
    <PreviewContainer
      sx={{
        aspectRatio: { xs: '16/7', sm: '16/6', md: '16/5' }, // Lebih memanjang horizontal
        minHeight: { xs: 120, sm: 160, md: 180 },
        maxHeight: { xs: 180, sm: 220, md: 260 },
        position: 'relative',
        width: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        border: '1px solid rgba(0,0,0,0.08)'
      }}
    >
      {selectedCard && (
        <>
          {isVideoFile(rows.find(row => row.NO_GAMBAR === selectedCard)?.GAMBAR || '') ? (
            <Box
              component='video'
              src={
                process.env.NEXT_PUBLIC_URL_IMAGE_TEMPLATE + rows.find(row => row.NO_GAMBAR === selectedCard)?.GAMBAR
              }
              sx={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 1,
                objectFit: 'cover'
              }}
              autoPlay
              loop
              muted
              playsInline
            />
          ) : (
            <Box
              component='img'
              src={
                process.env.NEXT_PUBLIC_URL_IMAGE_TEMPLATE + rows.find(row => row.NO_GAMBAR === selectedCard)?.GAMBAR
              }
              alt='Ucapan Duka'
              sx={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 1,
                objectFit: 'cover'
              }}
            />
          )}
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              textAlign: 'center',
              px: { xs: 1, sm: 2, md: 3 },
              py: { xs: 2, sm: 3, md: 4 },
              gap: { xs: 1.5, sm: 2, md: 3 } // spacing antar section
            }}
          >
            <Box>
              <Typography
                variant='h4'
                fontWeight='bold'
                sx={{
                  fontSize: { xs: '0.85rem', sm: '1rem', md: '1.2rem', lg: '1.3rem' },

                  color: textColor,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                }}
              >
                {detailForm.title}
              </Typography>
              <Typography
                variant='subtitle1'
                sx={{
                  fontWeight: 'bold',
                  mt: 1,
                  fontSize: { xs: '0.6rem', sm: '0.8rem', md: '0.9rem' },

                  color: textColor,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                }}
              >
                Atas Meninggalnya
              </Typography>
            </Box>

            <Box>
              <Typography
                variant='h3'
                fontWeight='bold'
                sx={{
                  mb: 3,
                  color: textColor,
                  fontSize: { xs: '0.6rem', sm: '0.8rem', md: '0.9rem' },

                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                }}
              >
                {dataRegis?.NAMA_M || '\u00A0'}
              </Typography>
              <Typography
                variant='subtitle1'
                fontWeight='bold'
                sx={{
                  color: textColor,
                  fontSize: { xs: '0.6rem', sm: '0.8rem', md: '0.9rem' },

                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                }}
              >
                {detailForm?.keteranganPenerima || '\u00A0'}
              </Typography>
              <Box sx={{ width: '60px', borderBottom: '2px solid white', my: 1, mx: 'auto', opacity: 0.8 }} />
              <Typography
                variant='subtitle1'
                fontWeight='bold'
                sx={{
                  color: textColor,
                  fontSize: { xs: '0.6rem', sm: '0.8rem', md: '0.9rem' },

                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                }}
              >
                {detailForm?.keteranganPengirim || '\u00A0'}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant='h5'
                fontWeight='bold'
                sx={{ color: textColor, textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}
              >
                {detailForm?.sender || '\u00A0'}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </PreviewContainer>
  )

  const handleDialogClose = (confirmed: boolean) => {
    setDialogOpen(false)

    if (confirmed) {
      toHome()
    }
  }

  
return (
    <>
      {isPending && <ProgressBar />}
      <ConfirmationDialogYes
        open={dialogOpen}
        onClose={handleDialogClose}
        title='Peringatan'
        content='Data ini sudah selesai pelayanan, anda tidak bisa membuat Funeral baru.'
      />

      <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', pb: 10 }}>
        <Container maxWidth='xl' sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant='h3'
              component='h1'
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                mb: 2
              }}
            >
              Order Funeral Digital
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              Kirimkan ucapan duka digital yang akan ditampilkan di ruangan persemayaman
            </Typography>
          </Box>

          {/* Stepper */}
          <StepperContainer
            sx={{
              px: { xs: 0.5, sm: 2, md: 3 },
              py: { xs: 1, sm: 2.5, md: 3 },
              mb: { xs: 1, md: 4 },
              overflowX: { xs: 'auto', sm: 'visible' }, // scroll horizontal di mobile
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.06)'
            }}
          >
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                minWidth: { xs: '600px', sm: 'auto' }, // lebar minimal agar bisa scroll di mobile
                flexWrap: { xs: 'nowrap', sm: 'wrap' },
                '.MuiStepConnector-root': {
                  minHeight: { xs: 24, sm: 32 }
                }
              }}
            >
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontSize: { xs: '0.7rem', sm: '0.85rem', md: '0.95rem', lg: '1rem' },
                        fontWeight: activeStep === index ? 600 : 400,
                        textAlign: 'center',
                        whiteSpace: 'normal',
                        lineHeight: 1.2,
                        maxWidth: { xs: 70, sm: 120, md: 160 }
                      }
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </StepperContainer>

          {/* Content */}
          <AnimatePresence mode='wait'>
            <ContentCard
              key={activeStep}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {activeStep === 0 && (
                <Grid container spacing={4}>
                  <Grid item xs={12} md={12}>
                    <Typography variant='h5' sx={{ fontWeight: 600, mb: 3 }}>
                      Informasi Pemesan
                    </Typography>
                    <Stack spacing={3}>
                      <TextField
                        label='Nomor Telepon'
                        fullWidth
                        required
                        value={personalInfo.phone}
                        onChange={async e => {
                          const phone = e.target.value

                          handlePersonalInfoChange('phone')(e)

                          if (/^08\d{8,11}$/.test(phone)) {
                            if (abortControllerRef.current) {
                              abortControllerRef.current.abort()
                            }

                            const controller = new AbortController()

                            abortControllerRef.current = controller
                            setHistoryList([])
                            setPersonalInfo(prev => ({ ...prev, firstName: '', email: '', alamat: '' }))
                            setPhoneLoading(false)
                            setPhoneLoading(true)

                            try {
                              const response = await axios.post(
                                urlAPI + 'registrasi/biodatapemesan',
                                { nohp: phone },
                                { signal: controller.signal }
                              )

                              if (response.data.metadata.code === 200) {
                                const fetchedData = response.data.response.list[0]

                                setHistoryList(response.data.response.list || [])
                                const { NAMA_PENGIRIM, EMAIL, ALAMAT_PENGIRIM } = fetchedData

                                setPersonalInfo(prev => ({
                                  ...prev,
                                  firstName: NAMA_PENGIRIM || prev.firstName,
                                  email: EMAIL || prev.email,
                                  alamat: ALAMAT_PENGIRIM || prev.alamat
                                }))
                              }
                            } catch (err: any) {
                              if (!axios.isCancel(err)) {
                                showToastAlert('error', 'Gagal mengambil data pemesan')
                              }
                            } finally {
                              setPhoneLoading(false)
                            }
                          }
                        }}
                        placeholder='08xxxxxxxxxx'
                        inputProps={{ maxLength: 13 }}
                        helperText={'Format: 08xxxxxxxxxx'}
                      />
                      <TextField
                        label='Nama Lengkap'
                        fullWidth
                        required
                        disabled={personalInfo.phone.length < 10 || phoneLoading}
                        value={personalInfo.firstName}
                        onChange={handlePersonalInfoChange('firstName')}
                        placeholder='Masukkan nama lengkap'
                        InputProps={{
                          endAdornment: phoneLoading ? (
                            <InputAdornment position='end'>
                              <CircularProgress size={20} color='primary' />
                            </InputAdornment>
                          ) : null
                        }}
                      />
                      <TextField
                        label='Alamat'
                        fullWidth
                        required
                        multiline
                        disabled={personalInfo.phone.length < 10 || phoneLoading}
                        rows={3}
                        value={personalInfo.alamat}
                        onChange={handlePersonalInfoChange('alamat')}
                        placeholder='Masukkan alamat lengkap'
                        InputProps={{
                          endAdornment: phoneLoading ? (
                            <InputAdornment position='end'>
                              <CircularProgress size={20} color='primary' />
                            </InputAdornment>
                          ) : null
                        }}
                      />
                      <TextField
                        label='Email'
                        fullWidth

                        // required
                        type='email'
                        disabled={personalInfo.phone.length < 10 || phoneLoading}
                        value={personalInfo.email}
                        onChange={handlePersonalInfoChange('email')}
                        placeholder='contoh@email.com'
                        InputProps={{
                          endAdornment: phoneLoading ? (
                            <InputAdornment position='end'>
                              <CircularProgress size={20} color='primary' />
                            </InputAdornment>
                          ) : null
                        }}
                      />
                    </Stack>
                  </Grid>
                  {historyList.length === 0 ? (
                    <></>
                  ) : (
                    <Grid item xs={12} md={12}>
                      <Typography variant='h5' sx={{ fontWeight: 600, mb: 3 }}>
                        History Pemesan
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Grid container spacing={2}>
                          {historyList.map((row, idx) => (
                            <Grid item xs={12} md={12} key={idx}>
                              <Card sx={{ mb: 2, boxShadow: 2 }}>
                                <CardContent>
                                  <Grid container spacing={2}>
                                    {/* Keterangan/Informasi di kiri */}
                                    <Grid item xs={12} sm={5}>
                                      <CardContent
                                        sx={{
                                          flex: 1,
                                          display: 'flex',
                                          flexDirection: 'column',
                                          justifyContent: 'center',
                                          px: { xs: 2, sm: 3 },
                                          py: { xs: 2, sm: 3 }
                                        }}
                                      >
                                        <Typography
                                          variant='subtitle2'
                                          color='primary'
                                          gutterBottom
                                          sx={{ fontWeight: 700 }}
                                        >
                                          Pengirim: {row.PENGIRIM}
                                        </Typography>
                                        <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                                          Penerima: <span style={{ fontWeight: 600 }}>{row.PENERIMA}</span>
                                        </Typography>
                                        {row.KET_PENERIMA && (
                                          <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                                            <span style={{ fontWeight: 500 }}>Keterangan Penerima:</span>{' '}
                                            {row.KET_PENERIMA}
                                          </Typography>
                                        )}
                                        {row.KET_PENGIRIM && (
                                          <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
                                            <span style={{ fontWeight: 500 }}>Keterangan Pengirim:</span>{' '}
                                            {row.KET_PENGIRIM}
                                          </Typography>
                                        )}
                                        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                                          <Chip label={`Durasi: ${row.DURASI} Detik`} color='primary' size='small' />
                                          <Chip label={`No. Order: ${row.NO_DUKA}`} color='default' size='small' />
                                        </Box>
                                        {/* Tombol aksi */}
                                        <Box sx={{ display: 'flex', gap: 1, mt: 3, justifyContent: 'flex-end' }}>
                                          <IconButton
                                            color='primary'
                                            size='small'
                                            sx={{
                                              borderRadius: '50%',
                                              border: '2px solid',
                                              borderColor: 'primary.main',
                                              backgroundColor: 'white',
                                              transition: 'all 0.2s',
                                              boxShadow: 'none',
                                              '&:hover': {
                                                backgroundColor: 'primary.50',
                                                borderColor: 'primary.dark'
                                              }
                                            }}
                                            onClick={() => {
                                              setDetailForm({
                                                title: row.JUDUL_UCAPAN,
                                                keteranganPenerima: row.KET_PENERIMA,
                                                keteranganPengirim: row.KET_PENGIRIM,
                                                sender: row.PENGIRIM
                                              })
                                              setSelectedCard(row.NO_GAMBAR)
                                              setSelectedPackage(row.ID_HARGA)
                                              setEditDukaId(row.NO_DUKA) // <-- simpan id duka yang diedit
                                              setSelectedBank(row.NOREK)
                                              setPreviewUrl(process.env.NEXT_PUBLIC_URL_IMAGE_BUKTI + row.BUKTI_BAYAR)
                                              setActiveStep(1)
                                            }}
                                            disabled={loading}
                                          >
                                            <EditIcon />
                                          </IconButton>
                                          <IconButton
                                            color='error'
                                            size='small'
                                            sx={{
                                              borderRadius: '50%',
                                              border: '2px solid',
                                              borderColor: 'error.main',
                                              backgroundColor: 'white',
                                              transition: 'all 0.2s',
                                              boxShadow: 'none',
                                              '&:hover': {
                                                backgroundColor: 'error.50',
                                                borderColor: 'error.dark'
                                              }
                                            }}
                                            onClick={() => {
                                              setDeleteDukaId(row.NO_DUKA)
                                              setDialogDeleteOpen(true)
                                            }}
                                            disabled={loading}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Box>
                                      </CardContent>
                                    </Grid>
                                    {/* Media Card di kanan */}
                                    <Grid item xs={12} sm={7}>
                                      <UcapanDukaMediaCard rowData={row} />
                                    </Grid>
                                  </Grid>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                  <ConfirmationDialog
                    open={dialogDeleteOpen}
                    onClose={async (confirmed: boolean) => {
                      if (confirmed) {
                        try {
                          await axios.post(urlAPI + 'registrasi/deletedukacita', { noduka: deleteDukaId })
                          showToastAlert('success', 'Data berhasil dihapus')
                          setHistoryList(prev => prev.filter(item => item.NO_DUKA !== deleteDukaId))
                        } catch (err) {
                          showToastAlert('error', 'Gagal menghapus data')
                        }
                      }

                      setDialogDeleteOpen(false)
                      setDeleteDukaId(null)
                    }}
                    title='Konfirmasi Hapus Data'
                    content='Apakah Anda yakin ingin menghapus ucapan duka ini?'
                  />
                  {/* <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: 'grey.50' }}>
                      <Typography variant='h6' sx={{ fontWeight: 600, mb: 3 }}>
                        Ringkasan Pesanan
                      </Typography>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography color='text.secondary'>Ucapan Duka</Typography>
                          <Typography sx={{ fontWeight: 600 }}>{detailForm.title}</Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography color='text.secondary'>Penerima</Typography>
                          <Typography sx={{ fontWeight: 600 }}>{dataRegis?.NAMA_M}</Typography>
                          {detailForm.keteranganPenerima && (
                            <Typography variant='body2' color='text.secondary'>
                              {detailForm.keteranganPenerima}
                            </Typography>
                          )}
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography color='text.secondary'>Pengirim</Typography>
                          <Typography sx={{ fontWeight: 600 }}>{detailForm.sender}</Typography>
                          {detailForm.keteranganPengirim && (
                            <Typography variant='body2' color='text.secondary'>
                              {detailForm.keteranganPengirim}
                            </Typography>
                          )}
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography color='text.secondary'>Paket Durasi</Typography>
                          <Typography sx={{ fontWeight: 600 }}>
                            {hargaUcapanList.find((item: any) => item.ID_HARGA === selectedPackage)?.KETERAANGAN} Detik
                          </Typography>
                          <Typography variant='h6' color='primary' sx={{ fontWeight: 700, mt: 1 }}>
                            Rp{' '}
                            {parseInt(
                              hargaUcapanList.find((item: any) => item.ID_HARGA === selectedPackage)?.HARGA || 0
                            ).toLocaleString('id-ID')}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid> */}
                </Grid>
              )}
              {activeStep === 1 && (
                <Grid container spacing={4}>
                  <Grid item xs={12} lg={9}>
                    <Typography variant='h5' sx={{ fontWeight: 600, mb: 3 }}>
                      Pilih Desain Ucapan Duka
                    </Typography>
                    <Grid container spacing={3}>
                      {rows.map((rowData, index) => (
                        <Grid item xs={12} sm={6} md={6} key={index}>
                          <DesignCard
                            onClick={() => {
                              setSelectedCard(selectedCard === rowData.NO_GAMBAR ? null : rowData.NO_GAMBAR)
                            }}
                            animate={{
                              scale: selectedCard === rowData.NO_GAMBAR ? 1.02 : 1,
                              borderColor: selectedCard === rowData.NO_GAMBAR ? '#1976d2' : 'transparent'
                            }}
                            whileTap={{ scale: 0.98 }}
                          >
                            {isVideoFile(rowData.GAMBAR) ? (
                              <video
                                src={process.env.NEXT_PUBLIC_URL_IMAGE_TEMPLATE + rowData.GAMBAR}
                                style={{ width: '100%', height: '60%', objectFit: 'fill' }}
                                muted
                                loop
                                autoPlay
                                playsInline
                              />
                            ) : (
                              <img
                                src={process.env.NEXT_PUBLIC_URL_IMAGE_TEMPLATE + rowData.GAMBAR}
                                alt={rowData.NO_GAMBAR}
                                style={{ width: '100%', height: '60%', objectFit: 'fill' }}
                                loading='lazy'
                              />
                            )}
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                background:
                                  selectedCard === rowData.NO_GAMBAR
                                    ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.9), rgba(21, 101, 192, 0.9))'
                                    : 'linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent)',
                                color: 'white',
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}
                            >
                              <Typography variant='h6' sx={{ fontWeight: 600, color: 'white' }}>
                                {rowData.NAMA_KATAGORI}
                              </Typography>
                              {selectedCard === rowData.NO_GAMBAR && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                                >
                                  <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 32, height: 32 }}>
                                    <CheckIcon fontSize='small' />
                                  </Avatar>
                                </motion.div>
                              )}
                            </Box>
                          </DesignCard>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>

                  <Grid item xs={12} lg={3}>
                    <Typography variant='h6' sx={{ fontWeight: 600, mb: 3 }}>
                      Filter Kategori
                    </Typography>
                    <Stack spacing={1}>
                      {kategoriTemplateList.map((kategori: any) => (
                        <CategoryToggle
                          key={kategori.ID_KATAGORI}
                          elevation={0}
                          onClick={() => handleToggle(String(kategori.ID_KATAGORI))}
                          sx={{
                            borderColor: selectedKategori.includes(String(kategori.ID_KATAGORI))
                              ? 'primary.main'
                              : 'rgba(0,0,0,0.08)',
                            backgroundColor: selectedKategori.includes(String(kategori.ID_KATAGORI))
                              ? 'primary.50'
                              : 'transparent'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Switch checked={selectedKategori.includes(String(kategori.ID_KATAGORI))} size='small' />
                            <Typography variant='body2' sx={{ fontWeight: 500 }}>
                              {kategori.NAMA_KATAGORI}
                            </Typography>
                          </Box>
                          <Chip
                            label={kategori.UNIT}
                            size='small'
                            variant='outlined'
                            color={selectedKategori.includes(String(kategori.ID_KATAGORI)) ? 'primary' : 'default'}
                          />
                        </CategoryToggle>
                      ))}
                    </Stack>
                  </Grid>
                </Grid>
              )}

              {activeStep === 2 && (
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant='h5' sx={{ fontWeight: 600, mb: 3 }}>
                      Atur Pesan Ucapan
                    </Typography>
                    <Stack spacing={3}>
                      <Autocomplete
                        value={detailForm.title}
                        onChange={(event, newValue) => {
                          setDetailForm(prev => ({ ...prev, title: newValue || '' }))
                        }}
                        options={condolencePhrases}
                        disableClearable
                        fullWidth
                        renderInput={params => (
                          <TextField
                            {...params}
                            label='Pilih kata-kata ucapan duka'
                            placeholder='Pilih kata-kata ucapan duka...'
                          />
                        )}
                      />
                      <TextField
                        label='Keterangan Penerima (Opsional)'
                        fullWidth
                        value={detailForm.keteranganPenerima}
                        onChange={handleFormChange('keteranganPenerima')}
                        placeholder='Contoh: Suami dari / Ayah dari / Ibu dari'
                        inputProps={{ maxLength: 50 }}
                        helperText={`${detailForm.keteranganPenerima.length}/50 karakter`}
                      />
                      <TextField
                        label='Keterangan Pengirim (Opsional)'
                        fullWidth
                        value={detailForm.keteranganPengirim}
                        onChange={handleFormChange('keteranganPengirim')}
                        placeholder='Contoh: Dari / Keluarga Besar / Direksi dan Staff'
                        inputProps={{ maxLength: 50 }}
                        helperText={`${detailForm.keteranganPengirim.length}/50 karakter`}
                      />
                      <TextField
                        label='Nama Pengirim'
                        fullWidth
                        required
                        value={detailForm.sender}
                        onChange={handleFormChange('sender')}
                        placeholder='Contoh: Keluarga Besar PT. ABC'
                        inputProps={{ maxLength: 50 }}
                        helperText={`${detailForm.sender.length}/50 karakter`}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
                      Preview Desain
                    </Typography>
                    {renderPreview()}
                  </Grid>
                </Grid>
              )}

              {activeStep === 3 && (
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant='h5' sx={{ fontWeight: 600, mb: 2 }}>
                      Pilih Paket Durasi
                    </Typography>
                    <Alert severity='info' sx={{ mb: 3 }}>
                      Pesan Anda akan ditampilkan di ruangan persemayaman sesuai durasi yang dipilih
                    </Alert>
                    <Stack spacing={2}>
                      {hargaUcapanList.map((item: any) => (
                        <PackageCard
                          key={item.ID_HARGA}
                          elevation={selectedPackage === item.ID_HARGA ? 4 : 1}
                          onClick={() => setSelectedPackage(item.ID_HARGA)}
                          sx={{
                            borderColor: selectedPackage === item.ID_HARGA ? 'primary.main' : 'transparent',
                            backgroundColor: selectedPackage === item.ID_HARGA ? 'primary.50' : 'white'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
                                Durasi {item.KETERAANGAN} Detik
                              </Typography>
                              <Typography variant='h4' color='primary' sx={{ fontWeight: 700 }}>
                                Rp {parseInt(item.HARGA).toLocaleString('id-ID')}
                              </Typography>
                            </Box>
                            <Switch checked={selectedPackage === item.ID_HARGA} color='primary' />
                          </Box>
                        </PackageCard>
                      ))}
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
                      Preview Desain
                    </Typography>
                    {renderPreview()}
                  </Grid>
                </Grid>
              )}

              {/* {activeStep === 3 && (
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant='h5' sx={{ fontWeight: 600, mb: 3 }}>
                      Informasi Pemesan
                    </Typography>
                    <Stack spacing={3}>
                      <TextField
                        label='Nama Lengkap'
                        fullWidth
                        required
                        value={personalInfo.firstName}
                        onChange={handlePersonalInfoChange('firstName')}
                        placeholder='Masukkan nama lengkap'
                      />
                      <TextField
                        label='Alamat'
                        fullWidth
                        required
                        multiline
                        rows={3}
                        value={personalInfo.alamat}
                        onChange={handlePersonalInfoChange('alamat')}
                        placeholder='Masukkan alamat lengkap'
                      />
                      <TextField
                        label='Email'
                        fullWidth
                        required
                        type='email'
                        value={personalInfo.email}
                        onChange={handlePersonalInfoChange('email')}
                        placeholder='contoh@email.com'
                      />
                      <TextField
                        label='Nomor Telepon'
                        fullWidth
                        required
                        value={personalInfo.phone}
                        onChange={handlePersonalInfoChange('phone')}
                        placeholder='08xxxxxxxxxx'
                        inputProps={{ maxLength: 13 }}
                        helperText='Format: 08xxxxxxxxxx'
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: 'grey.50' }}>
                      <Typography variant='h6' sx={{ fontWeight: 600, mb: 3 }}>
                        Ringkasan Pesanan
                      </Typography>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography color='text.secondary'>Ucapan Duka</Typography>
                          <Typography sx={{ fontWeight: 600 }}>{detailForm.title}</Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography color='text.secondary'>Penerima</Typography>
                          <Typography sx={{ fontWeight: 600 }}>{dataRegis?.NAMA_M}</Typography>
                          {detailForm.keteranganPenerima && (
                            <Typography variant='body2' color='text.secondary'>
                              {detailForm.keteranganPenerima}
                            </Typography>
                          )}
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography color='text.secondary'>Pengirim</Typography>
                          <Typography sx={{ fontWeight: 600 }}>{detailForm.sender}</Typography>
                          {detailForm.keteranganPengirim && (
                            <Typography variant='body2' color='text.secondary'>
                              {detailForm.keteranganPengirim}
                            </Typography>
                          )}
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography color='text.secondary'>Paket Durasi</Typography>
                          <Typography sx={{ fontWeight: 600 }}>
                            {hargaUcapanList.find((item: any) => item.ID_HARGA === selectedPackage)?.KETERAANGAN} Detik
                          </Typography>
                          <Typography variant='h6' color='primary' sx={{ fontWeight: 700, mt: 1 }}>
                            Rp{' '}
                            {parseInt(
                              hargaUcapanList.find((item: any) => item.ID_HARGA === selectedPackage)?.HARGA || 0
                            ).toLocaleString('id-ID')}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              )} */}

              {activeStep === 4 && (
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant='h5' sx={{ fontWeight: 600, mb: 3 }}>
                      Metode Pembayaran
                    </Typography>
                    <Stack spacing={2}>
                      {rekeningList.map((bank: any) => (
                        <PackageCard
                          key={bank.NOREK}
                          elevation={selectedBank === bank.NOREK ? 4 : 1}
                          onClick={() => setSelectedBank(bank.NOREK)}
                          sx={{
                            borderColor: selectedBank === bank.NOREK ? 'primary.main' : 'transparent',
                            backgroundColor: selectedBank === bank.NOREK ? 'primary.50' : 'white'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography variant='h6' sx={{ fontWeight: 600 }}>
                                {bank.NAMABANK}
                              </Typography>
                              <Typography variant='body1' sx={{ mt: 1 }}>
                                {bank.NOREK}
                              </Typography>
                              <Typography variant='body2' color='text.secondary'>
                                A/N: {bank.NAMAREK}
                              </Typography>
                            </Box>
                            <Switch checked={selectedBank === bank.NOREK} color='primary' />
                          </Box>
                        </PackageCard>
                      ))}
                    </Stack>

                    <Paper elevation={2} sx={{ p: 3, mt: 3, backgroundColor: 'primary.50' }}>
                      <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
                        Total Pembayaran
                      </Typography>
                      <Typography variant='h4' color='primary' sx={{ fontWeight: 700 }}>
                        Rp{' '}
                        {parseInt(
                          hargaUcapanList.find((item: any) => item.ID_HARGA === selectedPackage)?.HARGA || 0
                        ).toLocaleString('id-ID')}
                      </Typography>
                      <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                        Silakan transfer sesuai nominal di atas
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
                      Upload Bukti Pembayaran
                    </Typography>
                    <Box
                      component='label'
                      sx={{
                        border: '2px dashed',
                        borderColor: paymentProof ? 'success.main' : 'primary.main',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        minHeight: 300,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                        backgroundColor: paymentProof ? 'success.50' : 'grey.50',
                        '&:hover': {
                          backgroundColor: paymentProof ? 'success.100' : 'primary.50'
                        }
                      }}
                    >
                      <input type='file' accept='image/jpeg,image/png' hidden onChange={handleFileUpload} />

                      {previewUrl ? (
                        <Box sx={{ position: 'relative', width: '100%' }}>
                          <img
                            src={previewUrl}
                            alt='Preview'
                            style={{
                              maxWidth: '100%',
                              maxHeight: '250px',
                              objectFit: 'contain',
                              borderRadius: '8px'
                            }}
                          />
                          <IconButton
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.9)' }
                            }}
                            onClick={e => {
                              e.preventDefault()
                              setPaymentProof(null)
                              setPreviewUrl('')
                            }}
                          >
                            <CloseIcon sx={{ color: 'white' }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <>
                          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                          <Typography variant='h6' sx={{ mb: 1 }}>
                            Upload Bukti Pembayaran
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            Klik atau seret file ke sini
                          </Typography>
                          <Typography variant='caption' color='text.secondary' sx={{ mt: 1 }}>
                            Format: JPG/PNG, Maksimal 5MB
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              )}

              {activeStep === 5 && (
                <Box>
                  <Typography variant='h4' sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                    Konfirmasi Pesanan
                  </Typography>

                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={3} sx={{ p: 4 }}>
                        <Typography variant='h6' sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
                          Detail Desain & Pesan
                        </Typography>

                        <Stack spacing={3}>
                          <Box>
                            <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                              Desain Terpilih dengan Preview Pesan
                            </Typography>
                            <PreviewContainer
                              sx={{
                                aspectRatio: { xs: '16/7', sm: '16/6', md: '16/5' }, // Lebih memanjang horizontal
                                minHeight: { xs: 120, sm: 160, md: 180 },
                                maxHeight: { xs: 180, sm: 220, md: 260 },
                                position: 'relative',
                                width: '100%',
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                border: '1px solid rgba(0,0,0,0.08)'
                              }}
                            >
                              {selectedCard && (
                                <>
                                  {isVideoFile(rows.find(row => row.NO_GAMBAR === selectedCard)?.GAMBAR || '') ? (
                                    <Box
                                      component='video'
                                      src={
                                        process.env.NEXT_PUBLIC_URL_IMAGE_TEMPLATE +
                                        rows.find(row => row.NO_GAMBAR === selectedCard)?.GAMBAR
                                      }
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        zIndex: 1,
                                        objectFit: 'cover'
                                      }}
                                      autoPlay
                                      loop
                                      muted
                                      playsInline
                                    />
                                  ) : (
                                    <Box
                                      component='img'
                                      src={
                                        process.env.NEXT_PUBLIC_URL_IMAGE_TEMPLATE +
                                        rows.find(row => row.NO_GAMBAR === selectedCard)?.GAMBAR
                                      }
                                      alt='Selected Design'
                                      sx={{
                                        width: '100%',
                                        height: '100%',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        zIndex: 1,
                                        objectFit: 'cover'
                                      }}
                                    />
                                  )}

                                  {/* Text Overlay */}
                                  <Box
                                    sx={{
                                      position: 'relative',
                                      zIndex: 2,
                                      height: '100%',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'space-between',
                                      textAlign: 'center',
                                      px: { xs: 1, sm: 2, md: 3 },
                                      py: { xs: 2, sm: 3, md: 4 },
                                      gap: { xs: 1.5, sm: 2, md: 3 } // spacing antar section
                                    }}
                                  >
                                    <Box>
                                      <Typography
                                        variant='h6'
                                        fontWeight='bold'
                                        sx={{
                                          fontSize: { xs: '0.85rem', sm: '1rem', md: '1.2rem', lg: '1.3rem' },
                                          color: 'white',
                                          textShadow: '2px 2px 4px rgba(0,0,0,0.7)'
                                        }}
                                      >
                                        {detailForm.title}
                                      </Typography>
                                      <Typography
                                        variant='body2'
                                        sx={{
                                          fontWeight: 'bold',
                                          mt: 0.5,
                                          color: 'white',
                                          fontSize: { xs: '0.6rem', sm: '0.8rem', md: '0.9rem' },

                                          textShadow: '1px 1px 2px rgba(0,0,0,0.7)'
                                        }}
                                      >
                                        Atas Meninggalnya
                                      </Typography>
                                    </Box>

                                    <Box>
                                      <Typography
                                        variant='h5'
                                        fontWeight='bold'
                                        sx={{
                                          fontSize: { xs: '0.6rem', sm: '0.8rem', md: '0.9rem' },
                                          mb: 2,
                                          color: 'white',
                                          textShadow: '2px 2px 4px rgba(0,0,0,0.7)'
                                        }}
                                      >
                                        {dataRegis?.NAMA_M || '\u00A0'}
                                      </Typography>
                                      {detailForm?.keteranganPenerima && (
                                        <Typography
                                          variant='body2'
                                          fontWeight='bold'
                                          sx={{
                                            fontSize: { xs: '0.6rem', sm: '0.8rem', md: '0.9rem' },
                                            color: 'white',
                                            textShadow: '1px 1px 2px rgba(0,0,0,0.7)'
                                          }}
                                        >
                                          {detailForm?.keteranganPenerima}
                                        </Typography>
                                      )}
                                      <Box
                                        sx={{
                                          width: '40px',
                                          borderBottom: '1px solid white',
                                          my: 1,
                                          mx: 'auto',
                                          opacity: 0.8
                                        }}
                                      />
                                      {detailForm?.keteranganPengirim && (
                                        <Typography
                                          variant='body2'
                                          fontWeight='bold'
                                          sx={{
                                            fontSize: { xs: '0.6rem', sm: '0.8rem', md: '0.9rem' },
                                            color: 'white',
                                            textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
                                            mb: 0.5
                                          }}
                                        >
                                          {detailForm?.keteranganPengirim}
                                        </Typography>
                                      )}
                                    </Box>

                                    <Box>
                                      <Typography
                                        variant='h6'
                                        fontWeight='bold'
                                        sx={{
                                          fontSize: { xs: '0.6rem', sm: '0.8rem', md: '0.9rem' },
                                          color: 'white',
                                          textShadow: '2px 2px 4px rgba(0,0,0,0.7)'
                                        }}
                                      >
                                        {detailForm?.sender || '\u00A0'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </>
                              )}
                            </PreviewContainer>
                          </Box>

                          <Divider />

                          <Box>
                            <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                              Kata-kata Ucapan
                            </Typography>
                            <Typography variant='h6' sx={{ fontWeight: 600 }}>
                              {detailForm.title}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                              Penerima
                            </Typography>
                            <Typography variant='body1' sx={{ fontWeight: 500 }}>
                              {dataRegis?.NAMA_M}
                            </Typography>
                            {detailForm.keteranganPenerima && (
                              <Typography variant='body2' color='text.secondary'>
                                {detailForm.keteranganPenerima}
                              </Typography>
                            )}
                          </Box>

                          <Box>
                            <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                              Pengirim
                            </Typography>
                            <Typography variant='body1' sx={{ fontWeight: 500 }}>
                              {detailForm.sender}
                            </Typography>
                            {detailForm.keteranganPengirim && (
                              <Typography variant='body2' color='text.secondary'>
                                {detailForm.keteranganPengirim}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Stack spacing={3}>
                        <Paper elevation={3} sx={{ p: 3 }}>
                          <Typography variant='h6' sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                            Informasi Pemesan
                          </Typography>
                          <Stack spacing={2}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography color='text.secondary'>Nama Lengkap</Typography>
                              <Typography sx={{ fontWeight: 500 }}>{personalInfo.firstName}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography color='text.secondary'>Email</Typography>
                              <Typography sx={{ fontWeight: 500 }}>{personalInfo.email}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography color='text.secondary'>Telepon</Typography>
                              <Typography sx={{ fontWeight: 500 }}>{personalInfo.phone}</Typography>
                            </Box>
                            <Box>
                              <Typography color='text.secondary' gutterBottom>
                                Alamat
                              </Typography>
                              <Typography sx={{ fontWeight: 500 }}>{personalInfo.alamat}</Typography>
                            </Box>
                          </Stack>
                        </Paper>

                        <Paper elevation={3} sx={{ p: 3 }}>
                          <Typography variant='h6' sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                            Detail Pembayaran
                          </Typography>
                          <Stack spacing={2}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography color='text.secondary'>Paket Durasi</Typography>
                              <Typography sx={{ fontWeight: 500 }}>
                                {hargaUcapanList.find((item: any) => item.ID_HARGA === selectedPackage)?.KETERAANGAN}{' '}
                                Detik
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography color='text.secondary'>Metode Pembayaran</Typography>
                              <Typography sx={{ fontWeight: 500 }}>
                                {rekeningList.find((bank: any) => bank.NOREK === selectedBank)?.NAMABANK}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography color='text.secondary'>No. Rekening</Typography>
                              <Typography sx={{ fontWeight: 500 }}>
                                {rekeningList.find((bank: any) => bank.NOREK === selectedBank)?.NOREK}
                              </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant='h6' sx={{ fontWeight: 600 }}>
                                Total Pembayaran
                              </Typography>
                              <Typography variant='h5' color='primary' sx={{ fontWeight: 700 }}>
                                Rp{' '}
                                {parseInt(
                                  hargaUcapanList.find((item: any) => item.ID_HARGA === selectedPackage)?.HARGA || 0
                                ).toLocaleString('id-ID')}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>

                        <Paper elevation={3} sx={{ p: 3 }}>
                          <Typography variant='h6' sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                            Bukti Pembayaran
                          </Typography>
                          {previewUrl && (
                            <Box sx={{ textAlign: 'center' }}>
                              <img
                                src={previewUrl}
                                alt='Bukti Pembayaran'
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '200px',
                                  objectFit: 'contain',
                                  borderRadius: '8px',
                                  border: '1px solid #e0e0e0'
                                }}
                              />
                            </Box>
                          )}
                        </Paper>
                      </Stack>
                    </Grid>
                  </Grid>

                  <Alert severity='info' sx={{ mt: 4 }}>
                    <Typography variant='body2'>
                      Pastikan semua informasi sudah benar sebelum melanjutkan. Setelah konfirmasi, pesanan akan
                      diproses dan ucapan duka akan ditampilkan di ruangan persemayaman.
                    </Typography>
                  </Alert>
                </Box>
              )}

              {activeStep === 6 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                  >
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        bgcolor: 'success.main',
                        mx: 'auto',
                        mb: 3
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 40, color: 'white' }} />
                    </Avatar>
                  </motion.div>

                  <Typography variant='h4' sx={{ fontWeight: 700, mb: 2, color: 'success.main' }}>
                    Pesanan Berhasil Dibuat!
                  </Typography>
                  <Typography variant='body1' color='text.secondary' sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
                    Terima kasih telah memberikan ucapan duka. Pesanan Anda sedang diproses dan akan segera ditampilkan
                    di ruangan persemayaman.
                  </Typography>

                  <Paper elevation={3} sx={{ p: 4, maxWidth: 500, mx: 'auto', mb: 4 }}>
                    <Typography variant='h6' sx={{ fontWeight: 600, mb: 3 }}>
                      Detail Pesanan
                    </Typography>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color='text.secondary'>Nomor Pesanan</Typography>
                        <Typography sx={{ fontWeight: 600 }}>#{noInvoice}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color='text.secondary'>Total Pembayaran</Typography>
                        <Typography sx={{ fontWeight: 600 }} color='primary'>
                          Rp{' '}
                          {parseInt(
                            hargaUcapanList.find((item: any) => item.ID_HARGA === selectedPackage)?.HARGA || 0
                          ).toLocaleString('id-ID')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color='text.secondary'>Nama Pemesan</Typography>
                        <Typography sx={{ fontWeight: 600 }}>{personalInfo.firstName}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color='text.secondary'>Metode Pembayaran</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {rekeningList.find((bank: any) => bank.NOREK === selectedBank)?.NAMABANK}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  <CustomButton
                    variant='outlined'
                    startIcon={<PrintIcon />}
                    loading={false}
                    onClick={() =>
                      generatePDF({
                        personalInfo,
                        detailForm,
                        dataRegis,
                        selectedBank,
                        selectedPackage,
                        rekeningList,
                        hargaUcapanList
                      })
                    }
                  >
                    Download Bukti Pembayaran
                  </CustomButton>
                </Box>
              )}
            </ContentCard>
          </AnimatePresence>
        </Container>
      </Box>

      {/* Navigation Bar */}
      <NavigationBar>
        <Container maxWidth='xl'>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={4}>
              <CustomButton
                color='secondary'
                loading={loading}
                onClick={activeStep === 0 ? toHome : handleBack}
                disabled={activeStep === steps.length - 1}
                startIcon={activeStep === 0 ? <HomeIcon /> : <ArrowBackIcon />}
              >
                {activeStep === 0 ? 'Beranda' : 'Kembali'}
              </CustomButton>
            </Grid>

            <Grid item xs={4} sx={{ textAlign: 'center' }}>
              <Grid sx={{ textAlign: 'center', display: { xs: 'none', sm: 'block' } }}>
                <Typography variant='body2' color='text.secondary'>
                  Langkah {activeStep + 1} dari {steps.length}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {steps[activeStep]}
                </Typography>
              </Grid>
            </Grid>

            <Grid item xs={4} container alignItems='center' justifyContent='flex-end'>
              <CustomButton
                variant='contained'
                loading={loading}
                onClick={activeStep === steps.length - 1 ? toHome : handleNext}
                endIcon={activeStep === steps.length - 1 ? <HomeIcon /> : <ArrowForwardIcon />}
              >
                {activeStep === 5 ? 'Konfirmasi' : activeStep === steps.length - 1 ? 'Selesai' : 'Selanjutnya'}
              </CustomButton>
            </Grid>
          </Grid>
        </Container>
      </NavigationBar>
    </>
  )
}

export default ViewPage
